/**
 * Install-once lifecycle for a micropip package that editor helpers load on
 * first use (pyflakes for linting, Black for formatting).
 *
 * Concurrent callers await the same in-flight install instead of each firing
 * their own micropip.install(). A failed attempt is not cached, so a later
 * caller can retry; callers degrade gracefully when install() resolves false.
 * Shared so the linter and formatter cannot drift apart (EHQ-117).
 */
export class LazyPackageInstall {
  private installed = false;
  private inFlight?: Promise<boolean>;

  /**
   * @param micropip the Pyodide micropip module
   * @param spec requirement passed to micropip, e.g. `pyflakes==3.2.0`
   * @param owner prefix for the warning logged when install fails
   */
  constructor(
    private readonly micropip: any,
    private readonly spec: string,
    private readonly owner: string,
  ) {}

  get isInstalled(): boolean {
    return this.installed;
  }

  /** Resolves true once the package is installed, false if this attempt failed. */
  install(): Promise<boolean> {
    if (this.installed) {
      return Promise.resolve(true);
    }
    return (this.inFlight ??= (async () => {
      try {
        await this.micropip.install(this.spec);
        this.installed = true;
        return true;
      } catch (error) {
        console.warn(`${this.owner}: failed to install ${this.spec}`, error);
        this.inFlight = undefined;
        return false;
      }
    })());
  }

  /**
   * Record the package's state without installing, e.g. after finding it
   * already importable, or to reset it in tests. Forgets any in-flight install.
   */
  setInstalled(installed: boolean): void {
    this.installed = installed;
    this.inFlight = undefined;
  }
}
